<template>
  <v-card style="height: calc(100vh - 48px)">
    <v-card-text>
      <v-row class="mx-0">
        <v-col cols="2">
          <select-project
            v-model="project"
            :project-list-custom="projectList"
            :loading="isLoading"
          />
          <h4 class="mt-2">
            Game settings:
            <v-menu
              v-if="availableFields.length"
            >
              <template #activator="{on, attrs}">
                <v-btn
                  color="primary"
                  icon
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-icon>
                    mdi-plus-circle-outline
                  </v-icon>
                </v-btn>
              </template>

              <v-list>
                <v-list-item
                  v-for="field in availableFields"
                  :key="field"
                  @click="$set(gameSettings, field, false)"
                >
                  {{ field }}
                </v-list-item>
              </v-list>
            </v-menu>
          </h4>

          <template
            v-for="(_, key) in gameSettings"
          >
            <v-checkbox
              v-if="key !== 'rollover_allowed'"
              :key="key"
              v-model="gameSettings[key]"
              :label="key"
              class="align-center mt-1"
              hide-details
            >
              <template #append>
                <v-btn
                  v-if="Object.keys(gameSettings).length > 1"
                  icon
                  @click="$delete(gameSettings, key)"
                >
                  <v-icon color="error">mdi-close</v-icon>
                </v-btn>
              </template>
            </v-checkbox>
            <v-text-field
              v-else
              :key="key"
              v-model.number="gameSettings[key]"
              :label="key"
              min="0"
              type="number"
              hide-details
            >
              <template #append>
                <v-btn
                  v-if="Object.keys(gameSettings).length > 1"
                  icon
                  @click="$delete(gameSettings, key)"
                >
                  <v-icon color="error">mdi-close</v-icon>
                </v-btn>
              </template>
            </v-text-field>
          </template>

          <v-row class="mx-0 justify-end">
            <v-btn
              v-if="games.length"
              icon
              color="error"
              :loading="isLoading"
              @click="games = []"
            >
              <v-icon>
                close
              </v-icon>
            </v-btn>

            <v-btn
              icon
              color="primary"
              :disabled="!games.length"
              :loading="isLoading"
              @click="onUpdateConfirm"
            >
              <v-icon>
                check
              </v-icon>
            </v-btn>
          </v-row>
        </v-col>
        <v-col cols="10">
          <template v-if="!games.length">
            <v-textarea
              v-model="rawGames"
              label="Games"
              auto-grow
              class="accounts-list-textarea"
              :hint="`Games IDs separated with the comma`"
            />
            <v-btn
              class="mt-2 ml-auto"
              color="primary"
              @click="onRawDataConfirm"
            >
              Confirm
            </v-btn>
          </template>

          <v-data-iterator
            v-else
            dense
            :items="isShowingDiff ? updateDifference : games"
            :footer-props="{ 'items-per-page-options': [50, 100, -1] }"
            :items-per-page="50"

          >
            <template v-slot:header>
              <v-row>
                <v-col
                  cols="2"
                >
                  <h3>Game ids</h3>
                </v-col>
                <v-col
                  v-if="updateDifference.length"
                  cols="6"
                  class="ml-auto d-flex justify-end align-center"
                >
                  <v-switch
                    v-model="isShowingDiff"
                    text
                    hide-details
                    class="mt-0"
                    color="error"
                    label="Show unidentified game ids"
                  />
                  <v-tooltip top>
                    <template #activator="{on}">
                      <v-btn
                        v-on="on"
                        icon
                        color="primary"
                        class="ml-2"
                        @click="onCopyDiff"
                      >
                        <v-icon>
                          mdi-content-copy
                        </v-icon>
                      </v-btn>
                    </template>
                    <span>Copy unidentified game ids</span>
                  </v-tooltip>
                </v-col>
              </v-row>
            </template>


            <template v-slot:default="{items}">
              <v-row style="max-height: calc(100vh - 160px); overflow-y: auto;">
                <v-col
                  v-for="item in items"
                  :key="item"
                  cols="12"
                  sm="6"
                  md="3"
                  lg="2"
                >
                  <v-text-field
                    :value="item"
                    style="max-width: 15rem;"
                    class="my-2"
                    hide-details
                    readonly
                    dense
                  />
                </v-col>
              </v-row>
            </template>

          </v-data-iterator>
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<style lang="scss" scoped>

  .accounts-list-textarea {
    ::v-deep(textarea) {
      max-height: calc(100vh - 190px) !important;
      overflow-y: auto !important;
    }
  }

  .paymethods-autocomplete {
    ::v-deep(.v-input__control) {
      max-height: calc(100vh - 250px);
      overflow-y: auto;
      overflow-x: hidden;
    }

  }
</style>

<script>
import SelectProject from '@/components/core/SelectProject/SelectProject.vue'
import { xor, uniq } from 'lodash'
import { GAME_FIELDS } from '../consts'

export default {
  components: {
    SelectProject,
  },
  props: {
    isLoading: Boolean,
    updateDifference: {
      type: Array,
      required: true,
    },
    projectList: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      gameSettings: {
        allow_freespins: false,
        bonus_no_play: false,
        loyalty_allowed: false,
        rollover_allowed: 0,
      },
      GAME_FIELDS,
      project: null,
      rawGames: '',
      games: [],
      isShowingDiff: false,
    }
  },

  mounted() {
    this.project = this.projectList[0].value
  },

  computed: {
    availableFields() {
      return xor(Object.keys(this.gameSettings), GAME_FIELDS)
    },
  },

  methods: {
    onRawDataConfirm() {
      this.games = uniq(this.rawGames.split(',').map(id => Number(id.trim())).filter(Boolean))
    },
    onUpdateConfirm() {
      this.$root.$confirm(
        'Confirm',
        `Please confirm to set ${Object.keys(this.gameSettings).map(key => `${key}: ${this.gameSettings[key]}`).join(', ')} for the selected ${this.games.length} games`
      )
        .then(yes => {
          if (!yes) {
            return
          }

          this.$emit('bulk-update', {
            games: this.games,
            entity_id: this.project,
            ...this.gameSettings,
          })
        })
    },
    onCopyDiff() {
      navigator.clipboard.writeText(this.updateDifference.join(','))
        .then(() => {
          this.$showSuccess('Copied successfully')
        })
    }
  },
}
</script>
